import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/Admin/AdminLayout';
import { fetchWithSecurity } from '../../utils/security';
import { toast } from 'react-toastify';
import {
    Package, ArrowLeft, Save, Upload, Trash2,
    CheckCircle2, Plus, X, Globe, DollarSign,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

const ICONS = ['📦', '🏛️', '📑', '🗣️', '🗺️', '🏰', '🚨', '📊', '📱', '🤖', '🌐', '🔒', '🏢', '🎓', '💡', '🛡️', '📡', '⚙️', '🔑', '💼', '🌍', '📈', '🎯', '🔔', '📋'];

const TARGET_MARKETS = [
    { value: 'government', label: '🏢 სამთავრობო (Gov)', color: '#7c3aed' },
    { value: 'education', label: '🎓 განათლება (Edu)', color: '#059669' },
    { value: 'business', label: '💼 ბიზნეს (Business)', color: '#d97706' },
    { value: 'municipal', label: '🏛️ მუნიციპალური (Municipal)', color: '#2563eb' },
    { value: 'personal', label: '👤 პირადი (Personal)', color: '#0891b2' },
    { value: 'other', label: '🌍 სხვა (Other)', color: '#64748b' },
];

const PRICING_MODELS = [
    { value: 'one_time', label: 'ერთჯერადი ლიცენზია' },
    { value: 'subscription', label: 'თვიური/წლიური გამოწერა (SaaS)' },
    { value: 'one_time_plus_support', label: 'ლიცენზია + ყოველწლიური მხარდაჭერა (ARR)' },
    { value: 'quote', label: 'ინდივიდუალური ფასი მოთხოვნით (Custom Quote)' },
];

const BADGE_COLORS = [
    '#2563eb', '#7c3aed', '#059669', '#dc2626', '#d97706',
    '#0891b2', '#9333ea', '#4f46e5', '#0d9488', '#be185d',
    '#1d4ed8', '#15803d', '#b91c1c', '#92400e', '#6d28d9',
];

const emptyProduct = {
    category_id: '',
    name_ka: '', name_en: '',
    slug: '',
    tagline_ka: '', tagline_en: '',
    description_ka: '', description_en: '',
    icon: '📦', badge_color: '#6366f1',
    thumbnail: '',
    target_market: 'government',
    setup_price: '', monthly_price: '', annual_price: '',
    currency: 'GEL',
    pricing_model: 'one_time_plus_support',
    features_ka: [], features_en: [],
    tech_stack: '',
    demo_url: '', docs_url: '',
    is_active: 1, is_featured: 0,
    sort_order: 0,
};

const AdminProductEdit = () => {
    const { id } = useParams();
    const isNew = !id || id === 'add' || id === 'new';
    const { user, isSuperAdmin, loading: authLoading } = useAuth();
    const navigate = useNavigate();

    const [form, setForm] = useState({ ...emptyProduct });
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [activeTab, setActiveTab] = useState('general'); // 'general' | 'pricing' | 'features' | 'links'

    // Feature input states
    const [featureInputKa, setFeatureInputKa] = useState('');
    const [featureInputEn, setFeatureInputEn] = useState('');
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (!authLoading && !isSuperAdmin()) navigate('/admin');
    }, [authLoading, isSuperAdmin, navigate]);

    // Fetch product & categories
    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                const catRes = await fetchWithSecurity(`${API_URL}products.php?type=categories`);
                const catData = await catRes.json();
                if (Array.isArray(catData)) setCategories(catData);

                if (!isNew) {
                    const prodRes = await fetchWithSecurity(`${API_URL}products.php?id=${id}`);
                    const prodData = await prodRes.json();
                    if (prodData && !prodData.error) {
                        setForm({
                            ...emptyProduct,
                            ...prodData,
                            features_ka: Array.isArray(prodData.features_ka) ? prodData.features_ka : [],
                            features_en: Array.isArray(prodData.features_en) ? prodData.features_en : [],
                        });
                    } else {
                        toast.error('პროდუქტი ვერ მოიძებნა');
                        navigate('/admin/products');
                    }
                }
            } catch (err) {
                console.error('Error loading product data:', err);
                toast.error('მონაცემების ჩატვირთვა ვერ მოხერხდა');
            }
            setLoading(false);
        };

        if (user) loadData();
    }, [id, isNew, user, navigate]);

    // Thumbnail upload handler
    const handleThumbnailUpload = async (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;

        setUploadingImage(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const res = await fetchWithSecurity(`${API_URL}upload.php`, {
                method: 'POST',
                body: fd
            });
            const data = await res.json();
            if (data.url) {
                setForm(prev => ({ ...prev, thumbnail: data.url }));
                toast.success('სურათი წარმატებით აიტვირთა!');
            } else {
                toast.error(data.error || 'ატვირთვა ვერ მოხერხდა');
            }
        } catch (err) {
            console.error('Upload error:', err);
            toast.error('სურათის ატვირთვა ვერ მოხერხდა');
        }
        setUploadingImage(false);
    };

    // Features handlers
    const addFeatureKa = () => {
        if (featureInputKa.trim()) {
            setForm(prev => ({ ...prev, features_ka: [...prev.features_ka, featureInputKa.trim()] }));
            setFeatureInputKa('');
        }
    };

    const addFeatureEn = () => {
        if (featureInputEn.trim()) {
            setForm(prev => ({ ...prev, features_en: [...prev.features_en, featureInputEn.trim()] }));
            setFeatureInputEn('');
        }
    };

    const removeFeatureKa = (idx) => {
        setForm(prev => ({ ...prev, features_ka: prev.features_ka.filter((_, i) => i !== idx) }));
    };

    const removeFeatureEn = (idx) => {
        setForm(prev => ({ ...prev, features_en: prev.features_en.filter((_, i) => i !== idx) }));
    };

    // Save product
    const handleSave = async (e) => {
        if (e) e.preventDefault();
        if (!form.name_ka.trim() || !form.name_en.trim()) {
            toast.error('პროდუქტის სახელი ქართულად და ინგლისურად სავალდებულოა!');
            setActiveTab('general');
            return;
        }

        setSaving(true);
        try {
            const payload = {
                ...form,
                setup_price: form.setup_price ? parseFloat(form.setup_price) : 0,
                monthly_price: form.monthly_price ? parseFloat(form.monthly_price) : 0,
                annual_price: form.annual_price ? parseFloat(form.annual_price) : 0,
                category_id: form.category_id ? parseInt(form.category_id, 10) : null,
                sort_order: form.sort_order ? parseInt(form.sort_order, 10) : 0,
            };

            const url = `${API_URL}products.php`;
            const method = isNew ? 'POST' : 'PUT';

            const res = await fetchWithSecurity(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await res.json();

            if (data.success || data.id) {
                toast.success(isNew ? 'პროდუქტი წარმატებით შეიქმნა!' : 'პროდუქტი წარმატებით განახლდა!');
                navigate('/admin/products');
            } else {
                toast.error(data.error || 'შენახვა ვერ მოხერხდა');
            }
        } catch (err) {
            console.error('Save error:', err);
            toast.error('პროდუქტის შენახვა ვერ მოხერხდა');
        }
        setSaving(false);
    };

    const sectorObj = TARGET_MARKETS.find(t => t.value === form.target_market) || TARGET_MARKETS[0];
    const catObj = categories.find(c => String(c.id) === String(form.category_id));

    if (loading) {
        return (
            <AdminLayout title={isNew ? 'ახალი პროდუქტი' : 'პროდუქტის რედაქტირება'}>
                <div className="text-center py-5 text-muted">
                    <div className="spinner-border text-primary mb-2" />
                    <div>იტვირთება...</div>
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout title={isNew ? 'ახალი პროდუქტის დამატება' : `რედაქტირება: ${form.name_ka || 'პროდუქტი'}`}>
            {/* Top Bar with Back Button & Actions */}
            <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
                <div className="d-flex align-items-center gap-3">
                    <Link to="/admin/products" className="shadcn-btn shadcn-btn-secondary">
                        <ArrowLeft size={16} /> უკან კატალოგში
                    </Link>
                    <div>
                        <h4 className="fw-bold mb-0">
                            {isNew ? '📦 ახალი ციფრული პროდუქტის დამატება' : `✏️ ${form.name_ka}`}
                        </h4>
                        <div className="text-muted smallest">
                            {isNew ? 'შეავსეთ პროდუქტის მონაცემები, ფასები და მახასიათებლები' : `ID: #${form.id} • შეცვალეთ პროდუქტის პარამეტრები`}
                        </div>
                    </div>
                </div>

                <div className="d-flex align-items-center gap-2">
                    <button
                        type="button"
                        className="shadcn-btn shadcn-btn-primary"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        <Save size={16} /> {saving ? 'ინახება...' : 'შენახვა & გამოქვეყნება'}
                    </button>
                </div>
            </div>

            <div className="row g-4">
                {/* ── LEFT: FORM INPUTS ── */}
                <div className="col-12 col-xl-8">
                    {/* Navigation Tabs */}
                    <div className="shadcn-card p-2 mb-3">
                        <div className="d-flex gap-1 flex-wrap">
                            <button
                                type="button"
                                className={`shadcn-btn shadcn-btn-sm ${activeTab === 'general' ? 'shadcn-btn-primary' : 'shadcn-btn-secondary'}`}
                                onClick={() => setActiveTab('general')}
                            >
                                <Package size={14} /> ძირითადი ინფორმაცია & სექტორი
                            </button>
                            <button
                                type="button"
                                className={`shadcn-btn shadcn-btn-sm ${activeTab === 'pricing' ? 'shadcn-btn-primary' : 'shadcn-btn-secondary'}`}
                                onClick={() => setActiveTab('pricing')}
                            >
                                <DollarSign size={14} /> ფასები & ლიცენზია
                            </button>
                            <button
                                type="button"
                                className={`shadcn-btn shadcn-btn-sm ${activeTab === 'features' ? 'shadcn-btn-primary' : 'shadcn-btn-secondary'}`}
                                onClick={() => setActiveTab('features')}
                            >
                                <CheckCircle2 size={14} /> მახასიათებლები ({form.features_ka.length})
                            </button>
                            <button
                                type="button"
                                className={`shadcn-btn shadcn-btn-sm ${activeTab === 'links' ? 'shadcn-btn-primary' : 'shadcn-btn-secondary'}`}
                                onClick={() => setActiveTab('links')}
                            >
                                <Globe size={14} /> ბმულები & SEO
                            </button>
                        </div>
                    </div>

                    {/* ── TAB 1: GENERAL ── */}
                    {activeTab === 'general' && (
                        <div className="shadcn-card p-4">
                            <h5 className="fw-bold mb-3">ძირითადი პარამეტრები</h5>
                            <div className="row g-3">
                                {/* Icon & Badge Color Bar */}
                                <div className="col-12">
                                    <div className="p-3 rounded-3 bg-light border">
                                        <div className="row g-3 align-items-center">
                                            <div className="col-auto">
                                                <div className="d-flex align-items-center gap-3">
                                                    <div 
                                                        className="rounded-3 d-flex align-items-center justify-content-center" 
                                                        style={{ width: 60, height: 60, background: `${form.badge_color}25`, fontSize: 32 }}
                                                    >
                                                        {form.icon || '📦'}
                                                    </div>
                                                    <div>
                                                        <label className="smallest text-muted fw-bold d-block mb-1">ემოჯი-ხატულა:</label>
                                                        <div className="d-flex flex-wrap gap-1" style={{ maxWidth: '300px' }}>
                                                            {ICONS.map(ico => (
                                                                <button
                                                                    key={ico}
                                                                    type="button"
                                                                    className="btn p-1"
                                                                    style={{
                                                                        fontSize: '18px', lineHeight: 1,
                                                                        background: form.icon === ico ? `${form.badge_color}30` : 'transparent',
                                                                        border: form.icon === ico ? `2px solid ${form.badge_color}` : '2px solid transparent',
                                                                        borderRadius: '6px'
                                                                    }}
                                                                    onClick={() => setForm({ ...form, icon: ico })}
                                                                >{ico}</button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col">
                                                <label className="smallest text-muted fw-bold d-block mb-1">ბეჯის ფერი:</label>
                                                <div className="d-flex flex-wrap gap-2 align-items-center">
                                                    {BADGE_COLORS.map(color => (
                                                        <button
                                                            key={color}
                                                            type="button"
                                                            onClick={() => setForm({ ...form, badge_color: color })}
                                                            style={{
                                                                width: 26, height: 26, borderRadius: '50%',
                                                                background: color, border: form.badge_color === color ? '3px solid #000' : '2px solid #fff',
                                                                boxShadow: form.badge_color === color ? '0 0 0 2px ' + color : '0 1px 3px rgba(0,0,0,0.2)',
                                                                cursor: 'pointer'
                                                            }}
                                                        />
                                                    ))}
                                                    <input
                                                        type="color"
                                                        value={form.badge_color}
                                                        onChange={e => setForm({ ...form, badge_color: e.target.value })}
                                                        style={{ width: 28, height: 28, borderRadius: '50%', border: '2px solid #e2e8f0', cursor: 'pointer', padding: 0 }}
                                                        title="Custom ფერი"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Names */}
                                <div className="col-12 col-md-6">
                                    <label className="form-label fw-bold small">სახელი ქართულად 🇬🇪 <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        className="shadcn-input"
                                        value={form.name_ka}
                                        onChange={e => setForm({ ...form, name_ka: e.target.value })}
                                        placeholder="მაგ: მუნიციპალური ოფიციალური პორტალი"
                                    />
                                </div>
                                <div className="col-12 col-md-6">
                                    <label className="form-label fw-bold small">Name in English 🇬🇧 <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        className="shadcn-input"
                                        value={form.name_en}
                                        onChange={e => setForm({ ...form, name_en: e.target.value })}
                                        placeholder="e.g. Official Municipal Portal"
                                    />
                                </div>

                                {/* Taglines */}
                                <div className="col-12 col-md-6">
                                    <label className="form-label fw-bold small">ტეგლაინი ქართულად 🇬🇪</label>
                                    <input
                                        type="text"
                                        className="shadcn-input"
                                        value={form.tagline_ka}
                                        onChange={e => setForm({ ...form, tagline_ka: e.target.value })}
                                        placeholder="მოკლე სარეკლამო აღწერა..."
                                    />
                                </div>
                                <div className="col-12 col-md-6">
                                    <label className="form-label fw-bold small">Tagline in English 🇬🇧</label>
                                    <input
                                        type="text"
                                        className="shadcn-input"
                                        value={form.tagline_en}
                                        onChange={e => setForm({ ...form, tagline_en: e.target.value })}
                                        placeholder="Short marketing tagline..."
                                    />
                                </div>

                                {/* Category & Sector (Target Market) */}
                                <div className="col-12 col-md-6">
                                    <label className="form-label fw-bold small">კატეგორია</label>
                                    <select
                                        className="shadcn-input"
                                        value={form.category_id}
                                        onChange={e => setForm({ ...form, category_id: e.target.value })}
                                    >
                                        <option value="">-- კატეგორია არ არის --</option>
                                        {categories.map(c => (
                                            <option key={c.id} value={c.id}>{c.icon} {c.name_ka}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-12 col-md-6">
                                    <label className="form-label fw-bold small">
                                        სექტორი / სამიზნე ბაზარი (Sector)
                                    </label>
                                    <select
                                        className="shadcn-input"
                                        value={form.target_market}
                                        onChange={e => setForm({ ...form, target_market: e.target.value })}
                                    >
                                        {TARGET_MARKETS.map(t => (
                                            <option key={t.value} value={t.value}>{t.label}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Descriptions */}
                                <div className="col-12 col-md-6">
                                    <label className="form-label fw-bold small">სრული აღწერა ქართულად 🇬🇪</label>
                                    <textarea
                                        className="shadcn-input"
                                        rows={4}
                                        value={form.description_ka}
                                        onChange={e => setForm({ ...form, description_ka: e.target.value })}
                                        placeholder="პროდუქტის დეტალური აღწერა..."
                                    />
                                </div>
                                <div className="col-12 col-md-6">
                                    <label className="form-label fw-bold small">Full Description in English 🇬🇧</label>
                                    <textarea
                                        className="shadcn-input"
                                        rows={4}
                                        value={form.description_en}
                                        onChange={e => setForm({ ...form, description_en: e.target.value })}
                                        placeholder="Detailed description in English..."
                                    />
                                </div>

                                {/* Tech Stack */}
                                <div className="col-12 col-md-6">
                                    <label className="form-label fw-bold small">ტექნოლოგიური სტეკი (Tech Stack)</label>
                                    <input
                                        type="text"
                                        className="shadcn-input"
                                        value={form.tech_stack}
                                        onChange={e => setForm({ ...form, tech_stack: e.target.value })}
                                        placeholder="React, PHP, MySQL, Docker, PDF.js..."
                                    />
                                </div>

                                {/* Thumbnail with Direct File Upload */}
                                <div className="col-12 col-md-6">
                                    <label className="form-label fw-bold small">
                                        Thumbnail (სურათის ატვირთვა ან ბმული)
                                    </label>
                                    <div className="d-flex gap-2 align-items-center">
                                        <input
                                            type="text"
                                            className="shadcn-input flex-grow-1"
                                            value={form.thumbnail || ''}
                                            onChange={e => setForm({ ...form, thumbnail: e.target.value })}
                                            placeholder="URL ან ატვირთეთ ფაილი..."
                                        />
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            accept="image/*"
                                            className="d-none"
                                            onChange={handleThumbnailUpload}
                                        />
                                        <button
                                            type="button"
                                            className="shadcn-btn shadcn-btn-secondary"
                                            onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                            disabled={uploadingImage}
                                            title="ფაილის ატვირთვა"
                                        >
                                            <Upload size={14} />
                                            {uploadingImage ? 'იტვირთება...' : 'ატვირთვა'}
                                        </button>
                                        {form.thumbnail && (
                                            <button
                                                type="button"
                                                className="shadcn-btn shadcn-btn-secondary text-danger"
                                                onClick={() => setForm({ ...form, thumbnail: '' })}
                                                title="სურათის წაშლა"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        )}
                                    </div>
                                    {form.thumbnail && (
                                        <div className="mt-2 p-2 bg-light rounded border d-flex align-items-center gap-2">
                                            <img
                                                src={form.thumbnail.startsWith('http') ? form.thumbnail : `${API_URL}${form.thumbnail}`}
                                                alt="Preview"
                                                style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: '6px' }}
                                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                            />
                                            <span className="smallest text-muted text-truncate">{form.thumbnail}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Active & Featured Toggles */}
                                <div className="col-12 pt-2 border-top">
                                    <div className="d-flex gap-4 flex-wrap">
                                        <div className="form-check form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="activeSwitch"
                                                checked={form.is_active === 1}
                                                onChange={e => setForm({ ...form, is_active: e.target.checked ? 1 : 0 })}
                                            />
                                            <label className="form-check-label small fw-semibold" htmlFor="activeSwitch">
                                                ✅ აქტიური პროდუქტი (ხილულია საიტზე)
                                            </label>
                                        </div>

                                        <div className="form-check form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="featuredSwitch"
                                                checked={form.is_featured === 1}
                                                onChange={e => setForm({ ...form, is_featured: e.target.checked ? 1 : 0 })}
                                            />
                                            <label className="form-check-label small fw-semibold" htmlFor="featuredSwitch">
                                                ⭐ Featured (გამორჩეული პროდუქტი)
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── TAB 2: PRICING ── */}
                    {activeTab === 'pricing' && (
                        <div className="shadcn-card p-4">
                            <h5 className="fw-bold mb-3">ფასები და ლიცენზირების მოდელი</h5>
                            <div className="row g-3">
                                <div className="col-12">
                                    <label className="form-label fw-bold small">ლიცენზირების მოდელი</label>
                                    <select
                                        className="shadcn-input"
                                        value={form.pricing_model}
                                        onChange={e => setForm({ ...form, pricing_model: e.target.value })}
                                    >
                                        {PRICING_MODELS.map(pm => (
                                            <option key={pm.value} value={pm.value}>{pm.label}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="col-12 col-md-4">
                                    <label className="form-label fw-bold small">საწყისი საფასური / Setup (₾ GEL)</label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light text-muted small">₾</span>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={form.setup_price}
                                            onChange={e => setForm({ ...form, setup_price: e.target.value })}
                                            placeholder="0.00"
                                            min="0"
                                        />
                                    </div>
                                </div>

                                <div className="col-12 col-md-4">
                                    <label className="form-label fw-bold small">წლიური მხარდაჭერა / ARR (₾ GEL)</label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light text-muted small">₾</span>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={form.annual_price}
                                            onChange={e => setForm({ ...form, annual_price: e.target.value })}
                                            placeholder="0.00"
                                            min="0"
                                        />
                                    </div>
                                </div>

                                <div className="col-12 col-md-4">
                                    <label className="form-label fw-bold small">თვიური საფასური (₾ GEL)</label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light text-muted small">₾</span>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={form.monthly_price}
                                            onChange={e => setForm({ ...form, monthly_price: e.target.value })}
                                            placeholder="0.00"
                                            min="0"
                                        />
                                    </div>
                                </div>

                                {/* 3-Year Value Calculation */}
                                {(parseFloat(form.setup_price) > 0 || parseFloat(form.annual_price) > 0) && (
                                    <div className="col-12 mt-3">
                                        <div className="p-3 bg-light rounded border">
                                            <div className="small text-muted mb-1">სავარაუდო 3-წლიანი საკონტრაქტო ღირებულება (LTV):</div>
                                            <div className="fs-5 fw-bold text-success">
                                                ₾{(parseFloat(form.setup_price || 0) + parseFloat(form.annual_price || 0) * 3).toLocaleString()} GEL
                                            </div>
                                            <div className="smallest text-muted">
                                                (Setup: ₾{parseFloat(form.setup_price || 0).toLocaleString()} + 3 წელი x ₾{parseFloat(form.annual_price || 0).toLocaleString()})
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* ── TAB 3: FEATURES ── */}
                    {activeTab === 'features' && (
                        <div className="shadcn-card p-4">
                            <h5 className="fw-bold mb-3">ფუნქციონალი & მახასიათებლები</h5>
                            <div className="row g-4">
                                {/* Georgian features */}
                                <div className="col-12 col-md-6">
                                    <label className="form-label fw-bold small">
                                        მახასიათებლები ქართულად 🇬🇪 ({form.features_ka.length})
                                    </label>
                                    <div className="d-flex gap-2 mb-2">
                                        <input
                                            type="text"
                                            className="shadcn-input"
                                            value={featureInputKa}
                                            onChange={e => setFeatureInputKa(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addFeatureKa(); } }}
                                            placeholder="მაგ: WCAG 2.1 AA მისაწვდომობა"
                                        />
                                        <button type="button" className="shadcn-btn shadcn-btn-secondary" onClick={addFeatureKa}>
                                            <Plus size={14} />
                                        </button>
                                    </div>
                                    <div className="d-flex flex-column gap-1">
                                        {form.features_ka.map((feat, i) => (
                                            <div key={i} className="d-flex align-items-center justify-content-between p-2 bg-light rounded border small">
                                                <span>✓ {feat}</span>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-link text-danger p-0 ms-2"
                                                    onClick={() => removeFeatureKa(i)}
                                                >
                                                    <X size={13} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* English features */}
                                <div className="col-12 col-md-6">
                                    <label className="form-label fw-bold small">
                                        Features in English 🇬🇧 ({form.features_en.length})
                                    </label>
                                    <div className="d-flex gap-2 mb-2">
                                        <input
                                            type="text"
                                            className="shadcn-input"
                                            value={featureInputEn}
                                            onChange={e => setFeatureInputEn(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addFeatureEn(); } }}
                                            placeholder="e.g. WCAG 2.1 AA compliance"
                                        />
                                        <button type="button" className="shadcn-btn shadcn-btn-secondary" onClick={addFeatureEn}>
                                            <Plus size={14} />
                                        </button>
                                    </div>
                                    <div className="d-flex flex-column gap-1">
                                        {form.features_en.map((feat, i) => (
                                            <div key={i} className="d-flex align-items-center justify-content-between p-2 bg-light rounded border small">
                                                <span>✓ {feat}</span>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-link text-danger p-0 ms-2"
                                                    onClick={() => removeFeatureEn(i)}
                                                >
                                                    <X size={13} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── TAB 4: LINKS & SEO ── */}
                    {activeTab === 'links' && (
                        <div className="shadcn-card p-4">
                            <h5 className="fw-bold mb-3">ბმულები და სისტემური პარამეტრები</h5>
                            <div className="row g-3">
                                <div className="col-12 col-md-6">
                                    <label className="form-label fw-bold small">Slug (URL იდენტიფიკატორი)</label>
                                    <input
                                        type="text"
                                        className="shadcn-input"
                                        value={form.slug || ''}
                                        onChange={e => setForm({ ...form, slug: e.target.value })}
                                        placeholder="municipal-portal (ავტომატური თუ ცარიელია)"
                                    />
                                    <small className="text-muted smallest">
                                        გამოიყენება მისამართში: /products/{"{"}slug{"}"}
                                    </small>
                                </div>

                                <div className="col-12 col-md-6">
                                    <label className="form-label fw-bold small">სორტირების რიგი (Sort Order)</label>
                                    <input
                                        type="number"
                                        className="shadcn-input"
                                        value={form.sort_order || 0}
                                        onChange={e => setForm({ ...form, sort_order: e.target.value })}
                                        placeholder="0"
                                    />
                                </div>

                                <div className="col-12 col-md-6">
                                    <label className="form-label fw-bold small">Live Demo URL</label>
                                    <input
                                        type="url"
                                        className="shadcn-input"
                                        value={form.demo_url || ''}
                                        onChange={e => setForm({ ...form, demo_url: e.target.value })}
                                        placeholder="https://demo.orbitronix.ge"
                                    />
                                </div>

                                <div className="col-12 col-md-6">
                                    <label className="form-label fw-bold small">დოკუმენტაციის ბმული (Docs URL)</label>
                                    <input
                                        type="url"
                                        className="shadcn-input"
                                        value={form.docs_url || ''}
                                        onChange={e => setForm({ ...form, docs_url: e.target.value })}
                                        placeholder="https://docs.orbitronix.ge"
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* ── RIGHT: LIVE PREVIEW CARD ── */}
                <div className="col-12 col-xl-4">
                    <div className="position-sticky" style={{ top: '90px' }}>
                        <div className="shadcn-card p-3 mb-3">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                                <span className="small fw-bold text-muted text-uppercase">
                                    👁️ ცოცხალი გადახედვა (Live Preview)
                                </span>
                                {form.is_featured === 1 && (
                                    <span className="badge bg-warning text-dark smallest fw-bold">⭐ FEATURED</span>
                                )}
                            </div>

                            {/* Card Display */}
                            <div 
                                className="p-3 rounded-3 border position-relative"
                                style={{
                                    borderLeft: `5px solid ${form.badge_color || '#6366f1'}`,
                                    background: '#ffffff'
                                }}
                            >
                                <div className="d-flex align-items-start gap-3 mb-2">
                                    <div 
                                        className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                                        style={{ width: 48, height: 48, background: `${form.badge_color}20`, fontSize: 26 }}
                                    >
                                        {form.icon || '📦'}
                                    </div>
                                    <div className="overflow-hidden">
                                        <h6 className="fw-bold text-dark mb-0 text-truncate">
                                            {form.name_ka || 'პროდუქტის სახელი'}
                                        </h6>
                                        <div className="text-muted smallest text-truncate">
                                            {form.name_en || 'Product Name EN'}
                                        </div>
                                        {catObj && (
                                            <span 
                                                className="badge rounded-pill smallest mt-1" 
                                                style={{ background: `${catObj.color}20`, color: catObj.color, border: `1px solid ${catObj.color}40` }}
                                            >
                                                {catObj.icon} {catObj.name_ka}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {form.tagline_ka && (
                                    <p className="smallest text-muted mb-2 text-truncate">{form.tagline_ka}</p>
                                )}

                                {/* Sector badge */}
                                <div className="mb-2">
                                    <span 
                                        className="badge rounded-pill smallest"
                                        style={{ background: `${sectorObj.color}15`, color: sectorObj.color, border: `1px solid ${sectorObj.color}35` }}
                                    >
                                        {sectorObj.label}
                                    </span>
                                </div>

                                {/* Pricing box */}
                                <div className="p-2 rounded bg-light border mb-2 text-center">
                                    <div className="row g-0">
                                        {form.setup_price > 0 && (
                                            <div className="col border-end">
                                                <div className="fw-bold text-primary small">₾{parseFloat(form.setup_price).toLocaleString()}</div>
                                                <div className="smallest text-muted">Setup</div>
                                            </div>
                                        )}
                                        {form.annual_price > 0 && (
                                            <div className="col">
                                                <div className="fw-bold text-success small">₾{parseFloat(form.annual_price).toLocaleString()}</div>
                                                <div className="smallest text-muted">ARR/წელი</div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Features preview */}
                                {form.features_ka.length > 0 && (
                                    <div className="small mb-2">
                                        <div className="smallest text-muted fw-bold mb-1">მახასიათებლები:</div>
                                        {form.features_ka.slice(0, 3).map((f, i) => (
                                            <div key={i} className="smallest text-dark mb-1 d-flex align-items-center gap-1">
                                                <CheckCircle2 size={10} className="text-success flex-shrink-0" />
                                                <span className="text-truncate">{f}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Quick Save Card */}
                        <div className="shadcn-card p-3">
                            <button
                                type="button"
                                className="shadcn-btn shadcn-btn-primary w-100 justify-content-center mb-2"
                                onClick={handleSave}
                                disabled={saving}
                            >
                                <Save size={16} /> {saving ? 'ინახება...' : 'შენახვა'}
                            </button>
                            <Link
                                to="/admin/products"
                                className="shadcn-btn shadcn-btn-secondary w-100 justify-content-center"
                            >
                                გაუქმება
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
};

export default AdminProductEdit;
